import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, PackageX, Chrome } from 'lucide-react';
import type { WatchlistViewItem } from './types';
import { WatchlistCard } from './WatchlistCard';
import { WatchlistTable } from './WatchlistTable';
import { WatchlistControls, type StatusFilter, type ViewLayout } from './WatchlistControls';
import { DeleteWatchDialog } from './DeleteWatchDialog';
import { ProductDetailModal } from './ProductDetailModal';
import { UrlSubmitBar } from '../track/UrlSubmitBar';
import { apiClient } from '../../api/client';
import { Button } from '../../components/common';
import { getMerchantName } from '../../utils/merchants';
import { useLanguage } from '../../contexts/LanguageContext';

const LAYOUT_STORAGE_KEY = 'priceloss_watchlist_layout';

export const WatchlistDashboard: React.FC = () => {
  const queryClient = useQueryClient();
  const { t } = useLanguage();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [viewLayout, setViewLayout] = useState<ViewLayout>(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = window.localStorage.getItem(LAYOUT_STORAGE_KEY);
      if (stored === 'grid' || stored === 'table') return stored;
    }
    return 'grid';
  });

  const [selectedDetailWatch, setSelectedDetailWatch] = useState<WatchlistViewItem | null>(null);
  const [deletingWatch, setDeletingWatch] = useState<WatchlistViewItem | null>(null);

  const handleLayoutChange = (layout: ViewLayout) => {
    setViewLayout(layout);
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(LAYOUT_STORAGE_KEY, layout);
    }
  };

  // 1. Fetch Watchlist Query
  const { data, isLoading, isError } = useQuery({
    queryKey: ['watches'],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/v1/watches', {
        params: {
          query: {
            limit: 100,
            offset: 0,
          },
        },
      });
      if (error) {
        throw error;
      }
      return data;
    },
  });

  // Map API items to view models
  const watches: WatchlistViewItem[] = useMemo(() => {
    if (!data?.items) return [];
    return (data.items as any[]).map((item) => ({
      id: item.id,
      trackedUrlId: item.tracked_url_id,
      requestedUrl: item.normalized_url || '',
      productTitle: item.product_title || 'Untitled Product',
      merchantDomain: item.domain || 'Store',
      merchantName: getMerchantName(item.domain),
      establishedCurrency: item.currency || 'USD',
      currentPrice: item.current_price || '0.00',
      listPrice: item.list_price ?? null,
      discountPct: item.discount_percentage ? parseFloat(item.discount_percentage) : null,
      isActive: Boolean(item.is_active),
      trackabilityStatus: item.trackability_status || 'TRACKABLE',
      lastSuccessAt: item.last_success_at ?? null,
      alertRule: item.alert_rule
        ? {
            id: item.alert_rule.id,
            targetPrice: item.alert_rule.target_price ?? null,
            targetCurrency: item.alert_rule.target_currency,
            notifyOnAnyDrop: Boolean(item.alert_rule.notify_on_any_drop),
            minDropPct: item.alert_rule.min_drop_pct || 3.0,
            state: item.alert_rule.state || 'ARMED',
            isActive: Boolean(item.alert_rule.is_active),
          }
        : null,
    }));
  }, [data]);

  // 2. Toggle Pause Mutation
  const togglePauseMutation = useMutation({
    mutationFn: async (watch: WatchlistViewItem) => {
      const nextActive = !watch.isActive;
      const { data, error } = await apiClient.PATCH('/api/v1/watches/{id}', {
        params: { path: { id: watch.id } },
        body: { is_active: nextActive },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['watches'] });
    },
  });

  // 3. Delete Watch Mutation
  const deleteMutation = useMutation({
    mutationFn: async (watchId: string) => {
      const { error } = await apiClient.DELETE('/api/v1/watches/{id}', {
        params: { path: { id: watchId } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setDeletingWatch(null);
      queryClient.invalidateQueries({ queryKey: ['watches'] });
    },
  });

  // Filter & search computation
  const filteredWatches = useMemo(() => {
    return watches.filter((w) => {
      // Status filter
      if (statusFilter === 'active' && !w.isActive) return false;
      if (statusFilter === 'paused' && w.isActive) return false;

      // Search query filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesTitle = w.productTitle.toLowerCase().includes(q);
        const matchesDomain = w.merchantDomain.toLowerCase().includes(q);
        const matchesMerchant = (w.merchantName || '').toLowerCase().includes(q);
        if (!matchesTitle && !matchesDomain && !matchesMerchant) return false;
      }

      return true;
    });
  }, [watches, statusFilter, searchQuery]);

  const counts = useMemo(() => {
    const all = watches.length;
    const active = watches.filter((w) => w.isActive).length;
    const paused = all - active;
    return { all, active, paused };
  }, [watches]);

  return (
    <div className="space-y-6">
      {/* On-Web URL Track Form */}
      <UrlSubmitBar onWatchCreated={() => queryClient.invalidateQueries({ queryKey: ['watches'] })} />

      {/* Watchlist Controls */}
      <WatchlistControls
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        viewLayout={viewLayout}
        onViewLayoutChange={handleLayoutChange}
        counts={counts}
      />

      {/* Loading state */}
      {isLoading && (
        <div className="py-20 flex flex-col items-center justify-center gap-3 text-slate-400">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          <p className="text-xs">Loading tracked products...</p>
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="p-8 text-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl">
          <p className="text-sm text-rose-600 dark:text-rose-400">
            Failed to load your watchlist. Please check your connection and refresh.
          </p>
        </div>
      )}

      {/* Empty State: No watches */}
      {!isLoading && !isError && watches.length === 0 && (
        <div className="py-16 px-4 text-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs">
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto mb-4">
            <Plus className="w-6 h-6" />
          </div>
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-1">
            {t('dashboard.emptyTitle')}
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto mb-6">
            {t('dashboard.emptyDesc')}
          </p>
          <div className="flex items-center justify-center gap-3">
            <a href="https://chromewebstore.google.com" target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" className="gap-1.5">
                <Chrome className="w-4 h-4" />
                <span>Get Extension</span>
              </Button>
            </a>
          </div>
        </div>
      )}

      {/* Filter No Matches */}
      {!isLoading && watches.length > 0 && filteredWatches.length === 0 && (
        <div className="py-12 text-center bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl">
          <PackageX className="w-8 h-8 text-slate-400 mx-auto mb-2" />
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
            {t('watchlist.noMatch')}
          </p>
        </div>
      )}

      {/* Content Rendering: Grid vs Table */}
      {!isLoading && filteredWatches.length > 0 && (
        <>
          {viewLayout === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredWatches.map((watch) => (
                <WatchlistCard
                  key={watch.id}
                  watch={watch}
                  onTogglePause={(w) => togglePauseMutation.mutate(w)}
                  onDelete={(w) => setDeletingWatch(w)}
                  onViewHistory={(w) => setSelectedDetailWatch(w)}
                />
              ))}
            </div>
          ) : (
            <WatchlistTable
              watches={filteredWatches}
              onTogglePause={(w) => togglePauseMutation.mutate(w)}
              onDelete={(w) => setDeletingWatch(w)}
              onViewHistory={(w) => setSelectedDetailWatch(w)}
            />
          )}
        </>
      )}

      {/* Deletion Dialog */}
      <DeleteWatchDialog
        watch={deletingWatch}
        isOpen={!!deletingWatch}
        onClose={() => setDeletingWatch(null)}
        onConfirm={() => deletingWatch && deleteMutation.mutate(deletingWatch.id)}
        isLoading={deleteMutation.isPending}
      />

      {/* Product Details, Price History & Alert Setup Modal */}
      {selectedDetailWatch && (
        <ProductDetailModal
          watch={selectedDetailWatch}
          isOpen={!!selectedDetailWatch}
          onClose={() => setSelectedDetailWatch(null)}
        />
      )}
    </div>
  );
};
